/**
 * The only way a guest's browser is allowed to learn whether the server knows it.
 *
 * The account-side path posts a body the controller ignores and reads back an empty 204, so the
 * button can go on while nothing was stored. Nothing here trusts `response.ok`, a status class,
 * or an internal boolean: a call counts as success only when the server answered in one exact
 * shape, with the brand this page is served under, within a deadline that lives in this file.
 *
 * Three outcomes, never two. A refusal and a timeout lead to different faces: a refusal means the
 * server said no, a timeout means we do not know, and "we do not know" may never render as "on".
 */

export const VISITOR_PUSH_DEADLINE_MS = 10000;

export const SUCCESS = 'success';
export const REJECTED = 'rejected';
export const TIMEOUT = 'timeout';

export const VISITOR_PUSH_SUBSCRIBE_URL = '/visitor-notifications/subscribe';
export const VISITOR_PUSH_KNOWN_URL = '/visitor-notifications/known';
export const VISITOR_PUSH_DELETE_URL = '/visitor-notifications/delete';

const DEADLINE_EXPIRED = Symbol('visitor-push-deadline-expired');

/**
 * Races a promise against the deadline. The race is what makes the deadline real: aborting the
 * request is a courtesy to the network, but a fetch implementation that never settles and never
 * honours its signal would otherwise leave the caller hanging forever.
 */
export function withDeadline(promise, deadlineMs, onTimeout) {
  let timer = null;
  const expiry = new Promise((resolve) => {
    timer = setTimeout(() => {
      if (typeof onTimeout === 'function') {
        onTimeout();
      }
      resolve(DEADLINE_EXPIRED);
    }, deadlineMs);
  });

  // Cleared on both paths. Written with .then alone, a promise that rejects left the timer
  // running: it would fire minutes later and call onTimeout for a request that had already failed,
  // which in this file means aborting a controller nobody is waiting on. Harmless today and the
  // kind of thing that stops being harmless when somebody gives onTimeout something to do.
  return Promise.race([promise, expiry]).finally(() => {
    if (timer !== null) {
      clearTimeout(timer);
    }
  });
}

export function deadlineExpired(value) {
  return value === DEADLINE_EXPIRED;
}

function isPositiveInteger(value) {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

function isNonEmptyString(value) {
  return typeof value === 'string' && value.length > 0;
}

/**
 * The media type has to be JSON on its own terms. A charset parameter is ordinary and allowed;
 * text/html is what a login page, a maintenance page and a proxy error all look like.
 */
function mediaTypeOf(response) {
  if (!response || !response.headers || typeof response.headers.get !== 'function') {
    return null;
  }
  const raw = response.headers.get('content-type');
  if (typeof raw !== 'string') {
    return null;
  }
  return raw.split(';')[0].trim().toLowerCase();
}

function defaultCsrfToken() {
  if (typeof document === 'undefined' || !document.querySelector) {
    return null;
  }
  const tag = document.querySelector('meta[name="csrf-token"]');
  return tag ? tag.getAttribute('content') : null;
}

function rejected(reason) {
  return { outcome: REJECTED, reason, websiteId: null, subscriptionRef: null, known: null };
}

function timedOut(reason) {
  return { outcome: TIMEOUT, reason, websiteId: null, subscriptionRef: null, known: null };
}

/**
 * Performs the call and hands back a parsed payload only when the transport itself was sound.
 * Anything else is already a verdict, so no caller can accidentally read a body that never came.
 */
async function requestJson(url, body, { fetchImpl, deadlineMs, csrfToken }) {
  const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!doFetch) {
    return { verdict: rejected('no-fetch') };
  }

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };
  const token = csrfToken === undefined ? defaultCsrfToken() : csrfToken;
  if (isNonEmptyString(token)) {
    headers['X-CSRF-TOKEN'] = token;
  }

  const init = {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    redirect: 'follow',
    body: JSON.stringify(body),
  };
  if (controller) {
    init.signal = controller.signal;
  }

  let started;
  try {
    started = Promise.resolve(doFetch(url, init));
  } catch (error) {
    return { verdict: rejected('network') };
  }

  const settled = await withDeadline(
    started.then((response) => ({ response }), () => ({ networkError: true })),
    deadlineMs || VISITOR_PUSH_DEADLINE_MS,
    () => {
      if (controller && typeof controller.abort === 'function') {
        controller.abort();
      }
    },
  );

  if (deadlineExpired(settled)) {
    return { verdict: timedOut('deadline') };
  }
  if (settled.networkError) {
    return { verdict: rejected('network') };
  }

  const response = settled.response;
  if (!response || response.redirected === true) {
    return { verdict: rejected('redirected') };
  }
  if (mediaTypeOf(response) !== 'application/json') {
    return { verdict: rejected('media-type') };
  }

  let payload;
  try {
    payload = await response.json();
  } catch (error) {
    return { verdict: rejected('unparseable') };
  }
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    return { verdict: rejected('not-an-object') };
  }

  return { response, payload };
}

/**
 * H3 subscribe: 201 and nothing else, ok true, known true, a positive integer brand that matches
 * the page, and an opaque reference the server can be held to later.
 */
export async function subscribeOnServer({
  endpoint,
  p256dh,
  auth,
  websiteId,
  fetchImpl,
  deadlineMs,
  csrfToken,
  url = VISITOR_PUSH_SUBSCRIBE_URL,
}) {
  const attempt = await requestJson(
    url,
    // No consent version here on purpose. Which wording a visitor agreed to is something the
    // server knows and this browser does not, so it is not a thing this side gets to claim.
    { endpoint, p256dh, auth },
    { fetchImpl, deadlineMs, csrfToken },
  );
  if (attempt.verdict) {
    return attempt.verdict;
  }

  const { response, payload } = attempt;
  if (response.status !== 201) {
    return rejected('status');
  }
  if (payload.ok !== true) {
    return rejected('ok');
  }
  if (payload.known !== true) {
    return rejected('known');
  }
  if (!isPositiveInteger(payload.website_id)) {
    return rejected('website-id-shape');
  }
  if (payload.website_id !== websiteId) {
    return rejected('wrong-brand');
  }
  if (!isNonEmptyString(payload.subscription_ref)) {
    return rejected('subscription-ref');
  }

  return {
    outcome: SUCCESS,
    reason: null,
    websiteId: payload.website_id,
    subscriptionRef: payload.subscription_ref,
    known: true,
  };
}

/**
 * H3 known: 200, ok true, a real boolean, same brand. known false is an answer, not a failure:
 * it is how the client learns the server forgot this browser and the face must go off.
 */
export async function readKnownFromServer({
  endpoint,
  websiteId,
  fetchImpl,
  deadlineMs,
  csrfToken,
  url = VISITOR_PUSH_KNOWN_URL,
}) {
  const attempt = await requestJson(url, { endpoint }, { fetchImpl, deadlineMs, csrfToken });
  if (attempt.verdict) {
    return attempt.verdict;
  }

  const { response, payload } = attempt;
  if (response.status !== 200) {
    return rejected('status');
  }
  if (payload.ok !== true) {
    return rejected('ok');
  }
  if (typeof payload.known !== 'boolean') {
    return rejected('known-shape');
  }
  if (!isPositiveInteger(payload.website_id)) {
    return rejected('website-id-shape');
  }
  if (payload.website_id !== websiteId) {
    return rejected('wrong-brand');
  }

  return {
    outcome: SUCCESS,
    reason: null,
    websiteId: payload.website_id,
    subscriptionRef: null,
    known: payload.known,
  };
}

/**
 * H3 delete: 200 and known false. A delete that still reports the browser as known has not
 * deleted anything, whatever its status line says.
 */
export async function deleteOnServer({
  endpoint,
  websiteId,
  fetchImpl,
  deadlineMs,
  csrfToken,
  url = VISITOR_PUSH_DELETE_URL,
}) {
  const attempt = await requestJson(url, { endpoint }, { fetchImpl, deadlineMs, csrfToken });
  if (attempt.verdict) {
    return attempt.verdict;
  }

  const { response, payload } = attempt;
  if (response.status !== 200) {
    return rejected('status');
  }
  if (payload.ok !== true) {
    return rejected('ok');
  }
  if (payload.known !== false) {
    return rejected('still-known');
  }
  if (!isPositiveInteger(payload.website_id)) {
    return rejected('website-id-shape');
  }
  if (payload.website_id !== websiteId) {
    return rejected('wrong-brand');
  }

  return {
    outcome: SUCCESS,
    reason: null,
    websiteId: payload.website_id,
    subscriptionRef: null,
    known: false,
  };
}
